import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/ui';
import { DateTimeUtility } from '@core/utils';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isAdjacentMonth: boolean;
}

@Component({
  selector: 'app-calendar',
  imports: [CommonModule, IconComponent, TranslateModule],
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent implements OnInit {
  today = new Date();
  currentMonth: Date = new Date();
  weekDays: string[] = [];
  monthDays: CalendarDay[] = [];
  selectedDay: Date = new Date();
  years: number[] = [];
  showYearSelection = false; // flag to toggle year selection view
  @Input() pastDatesSelection = true;
  @Input() startDateSelectionFrom: string | null = null;
  @Output() dateSelected = new EventEmitter<string>();
  private readonly _translate = inject(TranslateService);

  @Input() set selectedDayInput(value: string | null) {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        this.selectedDay = date;
        this.currentMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      } else {
        this._resetToCurrentDate();
      }
    } else {
      this._resetToCurrentDate();
    }
    this.generateMonthDays(this.currentMonth);
  }

  get monthName() {
    const currentMonthNumber = this.currentMonth.getMonth() + 1;
    const currentMonthName = DateTimeUtility.getMonthName(currentMonthNumber, this._translate);

    return `${currentMonthName} ${this.currentMonth.getFullYear()}`;
  }

  ngOnInit() {
    this.today.setHours(0, 0, 0, 0);
    this._initializeData();
    if (!this.selectedDay) {
      this._resetToCurrentDate();
    }

    if (this.startDateSelectionFrom) {
      const startDate = new Date(this.startDateSelectionFrom);
      if (this.selectedDay < startDate) {
        this.selectedDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
        this.currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      }
    }

    this.generateMonthDays(this.currentMonth);
    this._generateYears();
  }

  toggleYearSelection() {
    if (!this.pastDatesSelection) return;

    this.showYearSelection = !this.showYearSelection;
    if (this.showYearSelection) {
      setTimeout(() => {
        const currentYearEl = document.getElementById('year-' + this.currentMonth.getFullYear());
        if (currentYearEl) {
          currentYearEl.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }, 0);
    }
  }

  selectYear(year: number) {
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    this.generateMonthDays(this.currentMonth);
    this.showYearSelection = false;
  }

  generateMonthDays(date: Date) {
    this.monthDays = [];
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const calendarDay = new Date(startOfMonth);
    calendarDay.setDate(calendarDay.getDate() - (calendarDay.getDay() || 7) + 1);

    do {
      this.monthDays.push({
        date: new Date(calendarDay),
        isCurrentMonth: calendarDay.getMonth() === date.getMonth(),
        isAdjacentMonth: calendarDay.getMonth() !== date.getMonth(),
      });
      calendarDay.setDate(calendarDay.getDate() + 1);
    } while (this.monthDays.length % 7 !== 0 || calendarDay.getMonth() === date.getMonth());
  }

  previousMonth() {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.generateMonthDays(this.currentMonth);
  }

  nextMonth() {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.generateMonthDays(this.currentMonth);
  }

  selectDay(day: CalendarDay) {
    if (!this.pastDatesSelection && day.date < this.today) {
      return;
    }

    if (this.startDateSelectionFrom && this.isInPreSelectedRange(day)) return;

    if (day.isCurrentMonth) {
      this.selectedDay = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate(), 12);
    }

    this.confirmDate();
  }

  isSelectedDay(day: CalendarDay): boolean {
    if (!this.selectedDay) return false;
    return day.date.toDateString() === this.selectedDay.toDateString();
  }

  isPreSelectedDay(day: CalendarDay): boolean {
    if (!this.startDateSelectionFrom) return false;
    const startDate = new Date(this.startDateSelectionFrom);
    return day.date.toDateString() === startDate.toDateString();
  }

  isInPreSelectedRange(day: CalendarDay): boolean {
    if (!this.startDateSelectionFrom) return false;
    const startDate = new Date(this.startDateSelectionFrom).setHours(0, 0, 0, 0);
    const currentDate = new Date(day.date).setHours(0, 0, 0, 0);
    return currentDate < startDate;
  }

  removeDate() {
    this.selectedDay = new Date();
    this.dateSelected.emit(null);
  }

  confirmDate() {
    if (this.selectedDay) {
      const localDate = new Date(this.selectedDay.getTime() - this.selectedDay.getTimezoneOffset() * 60000);
      this.dateSelected.emit(localDate.toISOString().split('T')[0]);
    }
  }

  private _resetToCurrentDate() {
    const currentDate = new Date();
    this.selectedDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    this.currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  }

  private _initializeData() {
    this.weekDays = [
      this._translate.instant('mo'),
      this._translate.instant('tu'),
      this._translate.instant('we'),
      this._translate.instant('th'),
      this._translate.instant('fr'),
      this._translate.instant('sa'),
      this._translate.instant('su'),
    ];
  }

  private _generateYears() {
    if (!this.pastDatesSelection) return;
    const currentYear = new Date().getFullYear();
    for (let y = 1900; y <= currentYear + 10; y++) {
      this.years.push(y);
    }
  }
}
